/**
 * `diagram/model.ts` — the in-memory model for an Aletheia interactive diagram
 * (interactive-diagrams-v0.1.md §5.1).
 *
 * The core contract (plan §1): **mermaid-in = mermaid-out**. A diagram is always a valid
 * vanilla mermaid block; Aletheia-specific geometry lives in `%% @aletheia:v1 {json}`
 * comment lines *inside* the fence. The `DiagramModel` is the bridge between that text and
 * the React Flow canvas — it captures the structured graph (nodes, edges, subgraphs) AND
 * the full ordered *provenance* of the original source (`statements`), so serialization can
 * be minimal-diff: an unedited statement re-emits its exact bytes, only edited/new elements
 * are re-generated canonically (plan §5.1 `serialize.ts`).
 *
 * The safety property that makes round-trip robust: any source line the parser does not
 * fully understand is retained as a `raw` statement and passes through verbatim. So
 * `serialize(parse(src)) === src` holds even for mermaid constructs outside our editable
 * subset — we simply cannot edit those on the canvas; we never corrupt them.
 *
 * This module is pure data + pure helpers — no mermaid, no React, no DOM. It is the shared
 * vocabulary the parser, serializer, sidecar, and (future) canvas all speak.
 */

/** The diagram families we recognize. Only `flowchart` / `graph` are *editable* in v0.1. */
export type DiagramType = 'flowchart' | 'graph' | 'unknown';

/**
 * Flowchart node shapes we model. The value is the canonical name; `SHAPE_DELIMS` maps each
 * to its mermaid open/close delimiters for serialization. `none` is a bare id reference
 * (`A` with no shape wrapper) — it carries no delimiters and no own label.
 */
export type NodeShape =
  | 'none'
  | 'rectangle' // A[label]
  | 'rounded' // A(label)
  | 'stadium' // A([label])
  | 'subroutine' // A[[label]]
  | 'cylinder' // A[(label)]
  | 'circle' // A((label))
  | 'rhombus' // A{label}
  | 'hexagon' // A{{label}}
  | 'parallelogram' // A[/label/]
  | 'parallelogram_alt' // A[\label\]
  | 'trapezoid' // A[/label\]
  | 'trapezoid_alt' // A[\label/]
  | 'asymmetric'; // A>label]

/**
 * Open/close delimiter pair per shape, longest-opener-first ordered for the parser's greedy
 * match (e.g. `([` must be tried before `(`). `none` has empty delimiters and is never
 * matched by the shape scanner — it is the fallback for a bare id.
 */
export const SHAPE_DELIMS: ReadonlyArray<{ shape: NodeShape; open: string; close: string }> = [
  { shape: 'stadium', open: '([', close: '])' },
  { shape: 'subroutine', open: '[[', close: ']]' },
  { shape: 'cylinder', open: '[(', close: ')]' },
  { shape: 'circle', open: '((', close: '))' },
  { shape: 'hexagon', open: '{{', close: '}}' },
  { shape: 'trapezoid', open: '[/', close: '\\]' },
  { shape: 'trapezoid_alt', open: '[\\', close: '/]' },
  { shape: 'parallelogram', open: '[/', close: '/]' },
  { shape: 'parallelogram_alt', open: '[\\', close: '\\]' },
  { shape: 'rectangle', open: '[', close: ']' },
  { shape: 'rounded', open: '(', close: ')' },
  { shape: 'rhombus', open: '{', close: '}' },
  { shape: 'asymmetric', open: '>', close: ']' },
];

/** The line style of an edge connector. */
export type ConnectorStyle = 'solid' | 'dotted' | 'thick';

/**
 * The terminal marker of an edge. `arrow` is `>`, `open` is none, `circle`/`cross` are the
 * `o`/`x` heads, `bi` is a bidirectional `<...>`.
 */
export type EdgeArrow = 'arrow' | 'open' | 'circle' | 'cross' | 'bi';

/** A graph node — either explicitly shaped or a bare-id reference. */
export interface DiagramNode {
  /** Author-supplied stable id. This is the key sidecar positions are anchored to. */
  id: string;
  /**
   * Display label. For `none` shape this equals the id (mermaid renders bare ids as their
   * own label). When a later definition supplies a shape+label, that wins over a bare ref.
   */
  label: string;
  /** The wrapper shape. `none` means the node was only ever referenced by bare id. */
  shape: NodeShape;
  /** Whether the label was quoted in source (`A["x]y"]`) — preserved for canonical emit. */
  quoted: boolean;
  /** `:::className` style-class references attached at a definition site, in source order. */
  classes: string[];
}

/** A directed edge. `ordinal` disambiguates parallel edges between the same (from,to) pair. */
export interface DiagramEdge {
  from: string;
  to: string;
  /** 0-based index among edges sharing this exact (from,to) pair, in document order. */
  ordinal: number;
  connector: ConnectorStyle;
  arrow: EdgeArrow;
  /** Edge label, if any (from either the inline `-- x -->` or pipe `-->|x|` form). */
  label?: string;
  /** Stable key: `"<from>-><to>:<ordinal>"` — the sidecar edge-override key (plan §3). */
  key: string;
}

/** A `subgraph … end` grouping. Membership is by node id, resolved at parse time. */
export interface Subgraph {
  /** Optional explicit id (`subgraph id [Title]`); falls back to the title or a synthesized id. */
  id: string;
  /** Optional display title. */
  title?: string;
  /** Node ids that appeared inside this subgraph block. */
  members: string[];
}

/** `"<from>-><to>:<ordinal>"` — the canonical edge key used by the sidecar and the canvas. */
export function edgeKey(from: string, to: string, ordinal: number): string {
  return `${from}->${to}:${ordinal}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidecar (`%% @aletheia:v1 {…}`) — geometry-only overrides (plan §3)
// ─────────────────────────────────────────────────────────────────────────────

/** `[x, y]` in canvas pixel coordinates. */
export type Point = [number, number];

/**
 * A free-floating **sticky note** — an annotation that is NOT part of the mermaid graph. It lives
 * only in the sidecar (Aletheia extension), so a vanilla mermaid renderer simply doesn't draw it
 * (graceful degradation, plan §3): the flowchart still renders, the note is silently dropped.
 */
export interface StickyNote {
  /** Stable annotation id (e.g. `s1`) — the sidecar key for this note. */
  id: string;
  /** The note's text (may be multi-line). */
  text: string;
  /** Top-left position in canvas pixels. */
  pos: Point;
  /** Manual size `[w, h]`; a sensible default is used when absent. */
  size?: Point;
  /** Portable accent hex (`#f59e0b`), applied as a tint + border. Default amber. */
  color?: string;
}

/**
 * A **swimlane / frame** — a labeled container band that organizes nodes visually. Like sticky
 * notes it is a sidecar-only Aletheia extension (not mermaid), and behaves as a whiteboard *frame*
 * (drop tiles on it) rather than an enforced parent — deliberately simple and fully portable.
 */
export interface Swimlane {
  /** Stable lane id (e.g. `lane1`) — the sidecar key for this lane. */
  id: string;
  /** Header label. */
  label: string;
  /** Top-left position in canvas pixels. */
  pos: Point;
  /** Size `[w, h]`. */
  size: Point;
  /** Portable accent hex for the header + tint. */
  color?: string;
  /** Header placement: `horizontal` → header band on top; `vertical` → header on the left. */
  orientation?: 'horizontal' | 'vertical';
}

/** The parsed, validated payload of the `%% @aletheia` sidecar. All fields are optional/sparse. */
export interface SidecarData {
  /** The sidecar schema version. v0.1 emits and understands `1`. */
  version: number;
  /** Optional stable diagram id — the MCP anchor (`get_diagram`/`upsert_diagram`). */
  id?: string;
  /** Node id → position. Absence means "auto-layout this node". */
  pos: Record<string, Point>;
  /** Node id → manual size `[w, h]`. */
  size: Record<string, Point>;
  /** Edge key → manual routing waypoints. */
  edge: Record<string, { wp: Point[] }>;
  /**
   * Sticky-note annotations (v0.4). Sidecar-only, not part of the mermaid graph — portable
   * renderers drop them; Aletheia draws them on the canvas.
   */
  notes?: StickyNote[];
  /** Swimlane / frame annotations (v0.4). Sidecar-only, same portability contract as `notes`. */
  lanes?: Swimlane[];
  /** Optional viewport memory. */
  canvas?: { zoom: number; center: Point };
}

/** An empty, valid sidecar payload at the current version. */
export function emptySidecar(): SidecarData {
  return { version: SIDECAR_VERSION, pos: {}, size: {}, edge: {} };
}

/** The sidecar schema version this build emits. */
export const SIDECAR_VERSION = 1;

// ─────────────────────────────────────────────────────────────────────────────
// Statement provenance — the ordered record that powers minimal-diff serialization
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single source statement, tagged by kind. Every statement carries its original `raw`
 * text (sans trailing newline). The serializer emits `raw` for anything not marked dirty,
 * which is what guarantees byte-stable round-trip (plan §5.1).
 *
 * `header` / `node` / `edge` / `subgraphStart` are the *structured* kinds the canvas can
 * edit. `comment` / `sidecar` / `directive` / `raw` are opaque-but-preserved.
 */
export type Statement =
  | { kind: 'header'; raw: string; diagramType: DiagramType; direction: string }
  | { kind: 'node'; raw: string; node: DiagramNode }
  | { kind: 'edge'; raw: string; nodes: DiagramNode[]; edges: DiagramEdge[] }
  | { kind: 'subgraphStart'; raw: string; id: string; title?: string }
  | { kind: 'subgraphEnd'; raw: string }
  | { kind: 'comment'; raw: string }
  | { kind: 'sidecar'; raw: string; data: SidecarData | null }
  /**
   * A native mermaid `style <id> k:v,k:v` node-style directive. Parsed (not opaque) so the
   * canvas can render + edit per-node styling while keeping it **native mermaid** — the plan's
   * §3 rule: color/font belong to mermaid syntax (portable to any renderer), never the sidecar.
   */
  | { kind: 'style'; raw: string; id: string; props: Record<string, string> }
  | { kind: 'directive'; raw: string }
  /** A line inside a leading `---` YAML frontmatter block (mermaid title/config). Preserved
   * verbatim (round-trips via `serialize`'s raw passthrough); never parsed as a node. */
  | { kind: 'frontmatter'; raw: string }
  | { kind: 'raw'; raw: string };

/**
 * The full parsed diagram (plan §5.1). `statements` is the provenance spine; `nodes`/`edges`/
 * `subgraphs` are the merged structured view built from it. `sidecar` is the merged geometry
 * payload (last-wins across multiple sidecar lines). `editable` is false for unsupported
 * diagram types — the caller renders those with vanilla mermaid and never opens the canvas.
 */
export interface DiagramModel {
  diagramType: DiagramType;
  direction: string;
  statements: Statement[];
  /** Merged node view keyed by id. A shaped definition supersedes a bare-id reference. */
  nodes: Map<string, DiagramNode>;
  /** All edges in document order, with computed ordinals + keys. */
  edges: DiagramEdge[];
  subgraphs: Subgraph[];
  /**
   * Merged per-node style props from native `style <id> …` directives, keyed by node id
   * (last directive wins per property). CSS-ish keys as mermaid uses them: `fill`, `stroke`,
   * `color`, `font-size`, `stroke-width`, …
   */
  styles: Map<string, Record<string, string>>;
  /**
   * Style-legend definitions from `classDef <name> k:v,…` directives, keyed by class name
   * (the mermaid `default` class, if defined, applies to every node). Resolved onto nodes by
   * {@link effectiveNodeStyle} so classDef colors render on the canvas + SVG export, not just
   * in the vanilla mermaid view.
   */
  classDefs: Map<string, Record<string, string>>;
  /**
   * Class assignments from `class <id1>,<id2> <name>` directive lines, keyed by node id.
   * Kept separate from a node's inline `:::` classes so serialization of an edited node never
   * double-emits them; {@link effectiveNodeStyle} unions both when resolving color.
   */
  classAssignments: Map<string, string[]>;
  /** Merged sidecar payload, or null if the source carried none. */
  sidecar: SidecarData | null;
  /** Title from a leading `---\ntitle: …\n---` frontmatter block, if present (WS1.3). Rendered as
   * the diagram's caption; undefined when the source has no frontmatter title. */
  title?: string;
  /** The original source verbatim — the anchor for minimal-diff serialization. */
  source: string;
  /** True only for `flowchart`/`graph`. False → treat as opaque, render vanilla. */
  editable: boolean;
}

/** The default flowchart direction mermaid assumes when a header omits one. */
export const DEFAULT_DIRECTION = 'TB';

/**
 * Resolve the effective style props for a node, cascading like mermaid does:
 *   `default` classDef  →  each of the node's classes (inline `:::` + `class …` lines)  →
 *   the per-node `style <id> …` directive (highest priority).
 * Returns a merged prop record (`fill`, `stroke`, `color`, `font-size`, …); empty when the node
 * has no styling. This is what lets classDef-based color legends render on the interactive canvas
 * and the SVG export — not only in the vanilla mermaid render.
 */
export function effectiveNodeStyle(
  model: DiagramModel,
  node: DiagramNode,
): Record<string, string> {
  const out: Record<string, string> = {};
  const apply = (props?: Record<string, string>): void => {
    if (props) Object.assign(out, props);
  };
  apply(model.classDefs.get('default'));
  const classes = [...node.classes, ...(model.classAssignments.get(node.id) ?? [])];
  for (const c of classes) apply(model.classDefs.get(c));
  apply(model.styles.get(node.id));
  return out;
}
